import type { Product } from '@recipes/contracts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
} from '@recipes/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { queryKeys } from '@/lib/query-keys';
import { patchProduct } from './catalog-queue';

/**
 * From the ingredient's side: "this product is one of these". Finds products by name, brand or
 * barcode and links the chosen one – a product that belonged to another ingredient moves.
 */
export function AssignProductDialog({
  ingredient,
  open,
  onOpenChange,
}: {
  ingredient: { id: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const q = useDebouncedValue(search.trim(), 250);
  const results = useQuery({
    queryKey: queryKeys.catalog.products({ q, assignTo: ingredient.id }),
    queryFn: () => api.products.list({ q, limit: 20 }),
    enabled: open && q.length >= 2,
  });
  const products = (results.data?.items ?? []).filter((p) => p.ingredient?.id !== ingredient.id);

  const assign = async (product: Product) => {
    setSaving(product.id);
    try {
      await patchProduct(product, { ingredientId: ingredient.id });
      await queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all });
      toast.success(
        t('catalog.ingredients.productAssigned', { product: product.name, name: ingredient.name }),
      );
      onOpenChange(false);
      setSearch('');
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="assign-product-dialog">
        <DialogHeader>
          <DialogTitle>{t('catalog.ingredients.assignProduct')}</DialogTitle>
          <DialogDescription>
            {t('catalog.ingredients.assignProductDescription', { name: ingredient.name })}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="assign-product-search" className="sr-only">
            {t('catalog.ingredients.assignProductSearch')}
          </Label>
          <Input
            id="assign-product-search"
            type="search"
            autoComplete="off"
            value={search}
            placeholder={t('catalog.ingredients.assignProductSearch')}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="assign-product-search"
          />
        </div>
        {q.length < 2 ? null : results.isPending ? (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-11" />
            <Skeleton className="h-11" />
          </div>
        ) : products.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="assign-product-none">
            {t('catalog.ingredients.assignProductNone')}
          </p>
        ) : (
          <ul className="max-h-[50vh] divide-y overflow-y-auto rounded-lg border">
            {products.map((product) => (
              <li key={product.id}>
                <button
                  type="button"
                  disabled={saving !== null}
                  className="flex min-h-[48px] w-full flex-col items-start px-3 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                  onClick={() => void assign(product)}
                  data-testid="assign-product-option"
                >
                  <span className="text-sm">
                    {product.name}
                    {product.brand && (
                      <span className="text-muted-foreground"> · {product.brand}</span>
                    )}
                  </span>
                  {product.ingredient && (
                    <span className="text-xs text-muted-foreground">
                      {t('catalog.ingredients.assignProductMoves', {
                        name: product.ingredient.name,
                      })}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
